import React from 'react';
import './AnswerProductsPanel.scss';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../../../../shared/components/common/tooltip/CustomTooltip';
import { MetadataBadge } from '../../../../../../../shared/components/metadata-badge/MetadataBadge';
import ProductCard from '../../../../../../../shared/components/product-card/ProductCard';
import { useGetAnswerFeatures } from '../../../../../../../shared/api/hooks/monitor/features/useGetAnswerFeatures';
import { ResponseMetadata } from '../../../../../../../shared/models/monitor/ResponseFeatures';

interface AnswerProductsPanelProps {
  monitorDataId: number;
  responseMetadata?: ResponseMetadata | null;
  /** Share of the period's answers containing shopping cards (header badge). */
  shoppingPeriodPercent: number | null;
}

const domainOf = (url?: string): string => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * "Shopping Cards" panel rendered in the RIGHT column of an expanded answer (below the brand
 * positions / ad slot), passed to ChatResponse via its `productsSlot`. The full list stacks
 * vertically — no carousel or inner scroll (product decision). Payloads are fetched lazily
 * (shared cache with AnswerAdSlot via the same monitorDataId query key).
 */
const AnswerProductsPanel: React.FC<AnswerProductsPanelProps> = ({
  monitorDataId,
  responseMetadata,
  shoppingPeriodPercent,
}) => {
  const { t } = useTranslation();
  const hasProducts = (responseMetadata?.productsCount ?? 0) > 0;
  const { features } = useGetAnswerFeatures(hasProducts ? monitorDataId : null);

  if (!hasProducts || !features || features.products.length === 0) {
    return null;
  }

  return (
    <div className="answer-products-panel">
      <div className="answer-products-panel__header">
        <span className="ico--shopping-cart ico--sm" />
        <span className="answer-products-panel__title">{t('shoppingCards')}</span>
        <CustomTooltip tooltipText="shoppingCardsTooltip" addMargin={false} />
        {shoppingPeriodPercent !== null && (
          <MetadataBadge
            variant="secondary"
            text={`${shoppingPeriodPercent}%`}
            tooltipText={t('shoppingCardsPercentTooltip')}
            uniqueId={`products-panel-badge-${monitorDataId}`}
          />
        )}
      </div>
      <div className="answer-products-panel__list">
        {features.products.map((product, index) => (
          <ProductCard
            key={`product-panel-${index}`}
            title={product.title}
            imageUrl={product.imageUrl}
            domain={product.domain ?? domainOf(product.url)}
            url={product.url}
            priceRaw={product.priceRaw}
            priceValue={product.priceValue}
            priceCurrency={product.priceCurrency}
          />
        ))}
      </div>
    </div>
  );
};

export default AnswerProductsPanel;
